/**
 * README
 * This is an "internal" component meant to be used solely by other RC components.
 * Please don't use this directly. If it seems like you'd benefit from it please
 * reach out to #cadence for help coordinating!
 */
import React, { cloneElement, useLayoutEffect } from 'react';
import classNames from 'classnames';
import {
  arrow,
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  size,
  safePolygon,
  useDelayGroup,
  useDismiss,
  useFloating,
  useId,
  useInteractions,
  useRole,
  useClick,
  limitShift,
  useHover,
  useTransitionStatus,
  Placement,
} from '@floating-ui/react';
import mergeRefs from 'react-merge-refs';
import { FLOATING_UI_DOM_ID, PANEL_DEFAULTS } from '@reverbdotcom/cadence/constants';

import { useRegisteredId } from '../../hooks/useRegisteredId';

/**
 * Lets content rendered inside the panel close the surface containing it.
 *
 * Internal — consumers reach this through `useRCPopover`, which is what RCPopover exports and the
 * only name they should need to know.
 *
 * Defaults to a no-op rather than `null` so a component reused both inside and outside a popover
 * doesn't crash on the outside path.
 */
export interface PopoverContextValue {
  close: () => void;
}

const RCPopoverBaseContext = React.createContext<PopoverContextValue>({ close: () => {} });

export const useRCPopoverBaseContext = () => React.useContext(RCPopoverBaseContext);

/**
 * How a popover-like surface finds its reference element — provide EXACTLY ONE:
 * - `anchor`: an element the surface clones + owns (gets the ref + interaction props), or
 * - `anchorRef`: a ref to an element the consumer already renders (positioning-only, nothing is
 *   cloned, no interaction props) so one trigger can be shared by two overlays (a menu + guide
 *   popover).
 * The discriminated union makes passing both — or neither — a compile error. Shared across the
 * popover wrappers (RCGuidePopover, etc.), which forward it by branching on the intact union so TS
 * narrows the sibling — no cast needed.
 */
export type PopoverAnchoring =
  | { anchor: JSX.Element; anchorRef?: never }
  | { anchor?: never; anchorRef: React.RefObject<HTMLElement> };

type PartsContextValue = ReturnType<typeof usePopoverBase>;

const PartsContext = React.createContext<PartsContextValue | null>(null);

function usePartsContext(part: string) {
  const value = React.useContext(PartsContext);
  if (!value) throw new Error(`RCPopoverBase.${part} must be rendered inside RCPopoverBase.Root.`);
  return value;
}

function arrowSide(panelPlacement) {
  // the side of the anchor that the panel sits on comes from `placement`
  const [ panelSide ] = panelPlacement.split('-');

  // the arrow sits on that opposite side, so we remap it here.
  const arrowSideMapping = {
    top: 'bottom',
    right: 'left',
    bottom: 'top',
    left: 'right',
  };

  return arrowSideMapping[panelSide];
}

function arrowPositionStyles(arrowData, panelPlacement) {
  return {
    top: arrowData?.y && `${arrowData?.y}px`,
    left: `${arrowData?.x}px`,

    // override defaults above based on position
    [arrowSide(panelPlacement)]: `-${PANEL_DEFAULTS.arrowHeight}px`,
  };
}

interface RootProps {
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  sticky?: boolean;
  position?: Placement;
  /** Toggles the panel when the trigger is clicked. */
  openOnClick?: boolean;
  /** Opens the panel on hover as well. Mouse only. */
  openOnHover?: boolean;
  /** Marks the trigger as controlling the panel (`aria-expanded`, `aria-controls`). Turn off for a
   * panel the trigger doesn't open. */
  triggerControlsPanel?: boolean;
  dismissOnOutsidePress?: boolean;
  children: React.ReactNode;
}

function usePopoverBase({
  isOpen: openProp,
  onOpenChange: setOpenProp,
  sticky,
  position,
  openOnClick,
  openOnHover,
  triggerControlsPanel,
  dismissOnOutsidePress,
}: Omit<RootProps, 'children'>) {
  const arrowRef = React.useRef(null);
  const [triggerId, setTriggerId] = React.useState<string>();
  const [headingId, setHeadingId] = React.useState<string>();
  const [descriptionId, setDescriptionId] = React.useState<string>();

  const [openState, setOpenState] = React.useState<boolean>(openProp || false);

  const isControlled = openProp !== undefined && setOpenProp !== undefined;
  const open = isControlled ? openProp : openState;
  const setOpen = isControlled ? setOpenProp : setOpenState;

  const handleOpenChange = React.useCallback((state: boolean) => {
    setOpen(state);
    if (!isControlled) {
      setOpenProp?.(state);
    }
  }, [setOpen, isControlled, setOpenProp]);

  const floating = useFloating({
    open,
    onOpenChange: handleOpenChange,
    strategy: 'fixed',
    middleware: [
      offset(PANEL_DEFAULTS.anchorOffset),
      flip(),
      shift({
        padding: {
          top: PANEL_DEFAULTS.arrowHeight,
          right: PANEL_DEFAULTS.windowEdgePadding,
          bottom: PANEL_DEFAULTS.windowEdgePadding,
          left: PANEL_DEFAULTS.windowEdgePadding,
        },
        mainAxis: true,
        crossAxis: true,
        limiter: sticky ? undefined : limitShift(),
      }),
      size({
        apply({ availableHeight, elements }) {
          const safeValue = Math.max(0, availableHeight);
          elements.floating.style.setProperty(
            '--available-height',
            `${safeValue / 10}rem`,
          );
        },
      }),
      arrow({
        element: arrowRef,
        padding: 16, // equal to border radius of popover panel
      }),
    ],
    placement: position,
    whileElementsMounted: (r, f, u) => {
      // We only need to auto update if it's possible to do so
      let cleanup = undefined;
      if (('ResizeObserver' in window)) {
        // animationFrame: true ensures the position stays correct when layout
        // shifts occur after initial render (e.g. ads or async content loading
        // above the reference element).
        cleanup = autoUpdate(r, f, u, { animationFrame: true });
      }
      return cleanup;
    },
  });
  const { context } = floating;

  // Prefer parent DelayGroup's delay values if available
  const { delay, currentId, isInstantPhase } = useDelayGroup(context);
  const transitionDelay = delay || {
    open: 350,
    close: 100,
  };

  const { isMounted, status } = useTransitionStatus(context, {
    duration: isInstantPhase ? {
      open: 0,
      close: currentId === context.floatingId ? 100 : 0,
    } : transitionDelay,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useRole(context, {
      enabled: triggerControlsPanel,
      role: 'dialog',
    }),
    useClick(context, {
      enabled: openOnClick,
      toggle: true,
    }),
    useHover(context, {
      enabled: openOnHover,
      delay: transitionDelay,
      mouseOnly: true,
      // safePolygon uses `performance.now()` – only use it if available on the window
      handleClose: (typeof window !== 'undefined' && 'performance' in window) ? safePolygon() : undefined,
    }),
    useDismiss(context, {
      enabled: true,
      escapeKey: true,
      outsidePressEvent: 'mousedown',
      outsidePress: dismissOnOutsidePress,
    }),
  ]);

  return {
    ...floating,
    arrowRef,
    isMounted,
    status,
    isInstantPhase,
    getReferenceProps,
    getFloatingProps,
    handleOpenChange,
    triggerId,
    setTriggerId,
    headingId,
    setHeadingId,
    descriptionId,
    setDescriptionId,
  };
}

/** Owns the open state, positioning and interactions that the other parts share. */
function Root({
  isOpen = undefined,
  onOpenChange = undefined,
  sticky = false,
  position = 'bottom-start',
  openOnClick = true,
  openOnHover = false,
  triggerControlsPanel = true,
  dismissOnOutsidePress = true,
  children,
}: RootProps) {
  const popover = usePopoverBase({
    isOpen,
    onOpenChange,
    sticky,
    position,
    openOnClick,
    openOnHover,
    triggerControlsPanel,
    dismissOnOutsidePress,
  });
  const { handleOpenChange } = popover;

  // Built on `handleOpenChange`, not the raw `setOpen`: closing from inside the panel has to run
  // the same path as an outside-press or Escape, or an uncontrolled popover would close without
  // ever firing the consumer's `onOpenChange`.
  const closeValue = React.useMemo(
    () => ({ close: () => handleOpenChange(false) }),
    [handleOpenChange],
  );

  return (
    <PartsContext.Provider value={popover}>
      <RCPopoverBaseContext.Provider value={closeValue}>
        {children}
      </RCPopoverBaseContext.Provider>
    </PartsContext.Provider>
  );
}

/**
 * The element that opens the panel and that it points to. Clones its only child, adding the ref and
 * interaction props, and keeps the child's id or gives it one so the panel can be named by it.
 */
function Trigger({ children }: { children: JSX.Element }) {
  const { refs, getReferenceProps, setTriggerId } = usePartsContext('Trigger');
  const generatedId = `${useId()}-trigger`;
  const id = children.props.id ?? generatedId;
  useRegisteredId(id, setTriggerId);

  if (children.type === React.Fragment) {
    // Fragments will render to the screen, but they lose their ref and the Popover UIs won't know what to anchor
    // themselves to, and the panel will end up in the top-left corner of the screen.
    throw new Error('Pass a single element to the `anchor` prop, Fragments or other arrays are not supported.');
  }

  // Preserve the consumer's ref
  const ref = mergeRefs([refs.setReference, (children as any).ref]);

  return cloneElement(children, {
    ...getReferenceProps({ ref, ...children.props, id }),
  });
}

/**
 * Positions the panel against an element the consumer already renders and owns, so one trigger can
 * be shared by two overlays. Nothing is cloned and no interaction props are added.
 */
function Anchor({ anchorRef }: { anchorRef: React.RefObject<HTMLElement> }) {
  const { refs } = usePartsContext('Anchor');

  // A layout effect runs after refs commit (so `.current` is populated) and before paint, so the
  // panel never flashes at (0,0). Intentionally no dependency array: refs are mutable, so we re-read
  // `.current` every commit — this re-points if the same RefObject is swapped to a new node.
  // `setReference` no-ops when the node is unchanged, so re-running is cheap.
  useLayoutEffect(() => {
    refs.setReference(anchorRef.current ?? null);
  });

  return null;
}

interface PanelProps {
  className?: string;
  focusOrder?: Array<'reference' | 'floating' | 'content'>;
  /** "Trapping" focus keeps the tab order inside the panel until it's dismissed. */
  trapFocus?: boolean;
  /** Index of the focusOrder array that should receive initial focus. Defaults to 0. Use -1 to prevent initial focus. */
  initialFocus?: -1 | 0 | 1 | 2;
  closeOnFocusOut?: boolean;
  children: React.ReactNode;
}

/**
 * The floating panel. It's named by a `Heading` inside it if there is one, otherwise by the
 * `Trigger`, and described only when a `Description` renders.
 */
function Panel({
  className = undefined,
  focusOrder = ['content'],
  trapFocus = true,
  initialFocus = 0,
  closeOnFocusOut = true,
  children,
}: PanelProps) {
  const {
    x,
    y,
    refs,
    strategy,
    context,
    placement,
    isMounted,
    status,
    isInstantPhase,
    getFloatingProps,
    triggerId,
    headingId,
    descriptionId,
  } = usePartsContext('Panel');

  if (!isMounted) return null;

  return (
    <FloatingPortal id={FLOATING_UI_DOM_ID}>
      <FloatingFocusManager
        context={context}
        modal={trapFocus}
        order={focusOrder}
        returnFocus
        initialFocus={initialFocus}
        closeOnFocusOut={closeOnFocusOut}
      >
        <div
          data-status={status}
          data-placement={placement}
          data-instant={isInstantPhase}
          ref={refs.setFloating}
          className={classNames('rc-popover-base', className)}
          style={{
            position: strategy,
            top: y ?? 0,
            left: x ?? 0,
          }}
          role="dialog"
          aria-labelledby={headingId ?? triggerId}
          aria-describedby={descriptionId}
          {...getFloatingProps()}
        >
          {children}
        </div>
      </FloatingFocusManager>
    </FloatingPortal>
  );
}

/** Names the panel. */
function Heading({ className = undefined, children }: { className?: string, children: React.ReactNode }) {
  const { setHeadingId } = usePartsContext('Heading');
  const id = useId();
  useRegisteredId(id, setHeadingId);

  return <h2 id={id} className={className}>{children}</h2>;
}

/** Describes the panel. Leave it out for structured or interactive content, which a screen reader
 * would otherwise read in full when the panel opens. */
function Description({ className = undefined, children }: { className?: string, children: React.ReactNode }) {
  const { setDescriptionId } = usePartsContext('Description');
  const id = useId();
  useRegisteredId(id, setDescriptionId);

  return <div id={id} className={className}>{children}</div>;
}

/** Points the panel at its trigger. */
function Arrow() {
  const { arrowRef, update, placement, middlewareData } = usePartsContext('Arrow');

  const arrowCallback = React.useCallback(
    (node) => {
      arrowRef.current = node;
      update();
    },
    [arrowRef, update],
  );

  return (
    <div
      className={`rc-popover-base__arrow rc-popover-base__arrow--${arrowSide(placement)}`}
      ref={arrowCallback}
      style={arrowPositionStyles(middlewareData?.arrow, placement)}
    />
  );
}

/** _Note: This is an **internal** component. Please reach out to #cadence before using._ */
export const RCPopoverBase = {
  Root,
  Trigger,
  Anchor,
  Panel,
  Heading,
  Description,
  Arrow,
};
